import React, {Component} from "react";
import {ReactSVG} from "react-svg";

import * as tooltipHelper from "../../../helpers/tooltipHelper";

export const BUTTON_VARIANT_PRIMARY = "MODAL_VARIANT_PRIMARY";
export const BUTTON_VARIANT_WARNING = "BUTTON_VARIANT_WARNING";
export const BUTTON_VARIANT_ERROR = "BUTTON_VARIANT_ERROR";
export const BUTTON_VARIANT_LINK = "BUTTON_VARIANT_LINK";

export const BUTTON_VARIANTS = [
    BUTTON_VARIANT_PRIMARY,
    BUTTON_VARIANT_WARNING,
    BUTTON_VARIANT_ERROR,
    BUTTON_VARIANT_LINK
];

export const BUTTON_EXTRA_SMALL = "BUTTON_EXTRA_SMALL";
export const BUTTON_SMALL = "BUTTON_SMALL";
export const BUTTON_MEDIUM = "BUTTON_MEDIUM";
export const BUTTON_LARGE = "BUTTON_LARGE";
export const BUTTON_EXTRA_LARGE = "BUTTON_EXTRA_LARGE";

export const BUTTON_SIZES = [BUTTON_EXTRA_SMALL, BUTTON_SMALL, BUTTON_MEDIUM, BUTTON_LARGE, BUTTON_EXTRA_LARGE];

/**
 * @class Button
 *
 * Generic button component
 *
 * [Confluence page]{@link https://deso.atlassian.net/wiki/spaces/Redlike/pages/51445809/Button}
 *
 * @param {string} [size] - button size
 * @param {string} [variant] - button variant
 * @param {string|React.ReactElement} [label] - data displayed on the button
 * @param {svg} [svgBefore] - SVG displayed before label
 * @param {svg} [svgAfter] - SVG displayed after label
 * @param {string} [tooltipText] - text message displayed on hover
 * @param {string} [className] - CSS class names to be applied to button
 * @param {boolean} [disabled] - disables the button if present or true (automatically removes any callable functions on button (except tooltip))
 * @param {object} [elementProps] - other properties passed to button element
 * @param {function} [onClick] - called on click
 * @param {function} [onDoubleClick] - called on double click
 * @param {function} [onMouseEnter] - called on mouse enter instead of creating tooltip
 *
 * @example Enabled
 *  <Button
 *      onClick={this.props.onClick}
 *      label={<Translate value="general.cancel" />}
 *      svgBefore={imgClose}
 *  />
 *
 *
 * @example Disabled
 *  <Button
 *      onClick={this.props.onClick}
 *      label={<Translate value="general.cancel" />}
 *      svgBefore={imgClose}
 *      disabled
 *  />
 *
 * @example Variant
 *  <Button
 *      className="force-show-svg"
 *      variant={BUTTON_VARIANT_WARNING}
 *      tooltipText={this.getValidationTooltipText()}
 *      onClick={this.handleQuickTintClick}
 *      svgBefore={WarningIcon}
 *      label="Dispense"
 *  />
 *
 *  @example Not square icon + forced icon
 *  <Button
 *      className="svg-ratio-2_1 force-show-svg"
 *      tooltipText={this.getDispensingBtnTooltipText()}
 *      onClick={this.handleQuickTintClick}
 *      svgBefore={iconHelper.getSvgIcon(componentTypes.ICON_DISPENSE)}
 *      label="Dispense"
 *  />
 *
 */
export default class Button extends Component {
    getButtonClass = () => {
        let variantClassName;
        switch (this.props.variant) {
            case BUTTON_VARIANT_PRIMARY:
                variantClassName = "primary-btn ";
                break;
            case BUTTON_VARIANT_WARNING:
                variantClassName = "warning-btn ";
                break;
            case BUTTON_VARIANT_ERROR:
                variantClassName = "error-btn ";
                break;
            case BUTTON_VARIANT_LINK:
                variantClassName = "link-btn ";
                break;
            default:
                variantClassName = "primary-btn ";
                break;
        }

        switch (this.props.size) {
            case BUTTON_EXTRA_SMALL:
                variantClassName += "xs-btn ";
                break;
            case BUTTON_SMALL:
                variantClassName += "s-btn ";
                break;
            case BUTTON_MEDIUM:
                variantClassName += "m-btn ";
                break;
            case BUTTON_LARGE:
                variantClassName += "l-btn ";
                break;
            case BUTTON_EXTRA_LARGE:
                variantClassName += "xl-btn ";
                break;
            default:
                variantClassName += "m-btn ";
                break;
        }

        return "general-btn " + variantClassName;
    };

    render() {
        return (
            <button
                className={
                    this.getButtonClass() + (this.props.disabled ? "disabled-btn " : "") + (this.props.className ?? "")
                }
                onClick={!this.props.disabled ? this.props.onClick : () => {}}
                onDoubleClick={!this.props.disabled ? this.props.onDoubleClick : () => {}}
                data-text={this.props.tooltipText}
                onMouseEnter={this.props.onMouseEnter ? this.props.onMouseEnter : tooltipHelper.createTooltip}
                {...this.props.elementProps}
                id={this.props.id}
            >
                {this.props.imgBefore}
                {this.props.svgBefore && <ReactSVG className="svg" src={this.props.svgBefore} />}
                {this.props.label ? <span>{this.props.label}</span> : null}
                {this.props.svgAfter && <ReactSVG className="svg" src={this.props.svgAfter} />}
            </button>
        );
    }
}
